import type { MenuItemPropsTypes, Tabs } from '@Shelly/shelly-design-system';
import type { TypeTabs } from '../types/Tabs';
import { devices } from 'src/stores/lists/devices';
import { rooms } from 'src/stores/lists/rooms';
import { location } from 'svelte-spa-router';
import { dashEdit, edit } from 'src/stores/ui/edit_lists';
import type { Device } from 'src/types/Device';
import { filterSensors } from 'src/components/Lists/list-helpers';
import { profile_data } from 'src/stores/account/user';
import { get, writable } from 'svelte/store';
import { showNewLayout } from 'src/stores/toDelete/new_layout';
import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';

export type MenuItemClickDeps = {
	isApp: boolean;
	openAddDevice: () => void;
	setEmptyDeviceListShow: (show: boolean) => void;
	openAiChat: () => void;
	trackAiFirstSeen: () => void;
};

export type MainMenuDockRegion = 'primary' | 'footer';

export type MenuItemDef = {
	id?: string;
	href?: string;
	icon: MenuItemPropsTypes['icon'];
	label: MenuItemPropsTypes['label'];
	labelDefault?: string;
	notification?: MenuItemPropsTypes['notification'];
	tabs?: Tabs;
	custom?: boolean;
	createOnClick?: (deps: MenuItemClickDeps) => () => void;
	/** Classic main rail only: pin item to the bottom with spacing from primary items */
	mainMenuDock?: MainMenuDockRegion;
	hideForSub?: boolean;
};

export type DrodpwonMenuSubmenuItemsTypes = {
	id: number;
	key: string;
	isGroupItem: boolean;
	title: string;
	defaultText: string;
	icon: string;
	active: boolean;
	showDropdown?: boolean;
};

export type MenuItems = MenuItemDef[];

export const mainMenuDefaultOrder = [0, 1, 2, 3, 4, 5];

/** Index into `menuItems` / persisted `menuOrder` for the classic rail settings tab. */
export const MAIN_MENU_ORDER_INDEX_SETTINGS = 3;
/** Index for the add-device footer action in persisted order. */
export const MAIN_MENU_ORDER_INDEX_ADD_DEVICE = 4;
/** Index into `menuItems` / persisted `menuOrder` for the classic rail assistant tab. */
export const MAIN_MENU_ORDER_INDEX_ASSISTANT = 5;

/**
 * Normalise a persisted classic-rail `menuOrder`: keep only valid `menuItems`
 * indices and drop duplicates. The current `menuItems` layout matches the
 * historical one (dash, home, energy, settings, add-device, assistant), so no
 * index remapping is required and previously persisted orders pass through.
 */
export function migrateClassicMenuOrderIndices(order: readonly number[]): number[] {
	const seen = new Set<number>();
	const result: number[] = [];
	for (const idx of order) {
		if (!Number.isInteger(idx) || idx < 0 || idx >= menuItems.length) continue;
		if (seen.has(idx)) continue;
		seen.add(idx);
		result.push(idx);
	}
	return result;
}

/**
 * Parses `profile_data.menuOrder` JSON safely. Invalid or non-array payloads fall
 * back to defaults. Always runs through `migrateClassicMenuOrderIndices` so
 * every consumer sees a valid current-layout order.
 */
export function parseHomeDataMenuOrderJson(menuOrderJson: string | undefined): number[] {
	if (!menuOrderJson || typeof menuOrderJson !== 'string') {
		return [...mainMenuDefaultOrder];
	}
	try {
		const parsed: unknown = JSON.parse(menuOrderJson);
		if (
			!Array.isArray(parsed) ||
			!parsed.every((n) => typeof n === 'number' && Number.isInteger(n))
		) {
			return [...mainMenuDefaultOrder];
		}
		return migrateClassicMenuOrderIndices(parsed as number[]);
	} catch {
		return [...mainMenuDefaultOrder];
	}
}

export function appendMissingMenuOrderIndices(order: readonly number[], items: MenuItems): number[] {
	const result = order.filter((idx) => Number.isInteger(idx) && idx >= 0 && idx < items.length);
	for (let idx = 0; idx < items.length; idx += 1) {
		if (!result.includes(idx)) result.push(idx);
	}
	return result;
}

export const MENU_ITEMS_IDS = {
	home: 'home',
	eventLog: 'eventLog',
	discounts: 'discounts',
	dash: 'dash',
	energy: 'energy',
	settings: 'settings',
	exploreShelly: 'exploreShelly',
	subscription: 'subscription',
	assistant: 'assistant',
	addDevice: 'addDevice',
	cameraMonitoring: 'cameraMonitoring',
	energyMonitoring: 'energyMonitoring',
};

export const menuItems: MenuItems = [
	{
		id: MENU_ITEMS_IDS.dash,
		href: '/dash',
		icon: 'fa-thin fa-tachometer-alt-fast',
		label: 'label_menu_items_dashboard',
		labelDefault: 'Dashboard',
	},
	{
		id: MENU_ITEMS_IDS.home,
		href: '/home',
		icon: 'fa-thin fa-home',
		label: 'label_menu_items_my_home',
		labelDefault: 'My home',
	},
	{
		id: MENU_ITEMS_IDS.energy,
		href: '/dash/energy_monitoring',
		icon: 'fa-thin fa-bolt',
		label: 'label_menu_items_energy',
		labelDefault: 'Energy',
	},
	{
		id: MENU_ITEMS_IDS.settings,
		href: '/settings',
		icon: 'fa-thin fa-cog',
		label: 'label_menu_items_settings',
		labelDefault: 'Settings',
	},
	{
		id: MENU_ITEMS_IDS.addDevice,
		custom: true,
		href: '/include_devices',
		icon: 'fas fa-plus',
		label: 'label_menu_items_device',
		labelDefault: 'Add device',
		mainMenuDock: 'footer',
		createOnClick: (deps) => () => {
			if (deps.isApp) deps.openAddDevice();
			deps.setEmptyDeviceListShow(true);
		},
	},
	{
		id: MENU_ITEMS_IDS.assistant,
		href: '/assistant',
		icon: 'fa-thin fa-sparkles',
		label: 'label_menu_items_assistant',
		mainMenuDock: 'primary',
		labelDefault: 'Shelly AI Assistant',
		hideForSub: true,
	},
];

/** Split persisted menu order into primary rail vs footer-docked items (classic layout). */
export function partitionMainMenuOrderIndices(
	order: readonly number[],
	items: MenuItems,
): { primary: number[]; footer: number[] } {
	const primary: number[] = [];
	const footer: number[] = [];
	for (const idx of order) {
		if (!Number.isInteger(idx) || idx < 0 || idx >= items.length) continue;
		if (items[idx].mainMenuDock === 'footer') footer.push(idx);
		else primary.push(idx);
	}
	footer.sort((a, b) => a - b);
	return { primary, footer };
}

export const MORE_MENU_ICON = 'far fa-ellipsis';

export const menuItemsNewLayout: MenuItems = [
	{
		id: MENU_ITEMS_IDS.dash,
		href: '/dash',
		icon: 'fas fa-objects-column',
		label: 'label_menu_items_dashboard',
		labelDefault: 'Dashboard',
	},
	{
		id: MENU_ITEMS_IDS.home,
		href: '/home',
		icon: 'fas fa-home',
		label: 'label_menu_items_my_home',
		labelDefault: 'My home',
	},
	{
		id: MENU_ITEMS_IDS.energy,
		href: '/dash/energy_monitoring',
		icon: 'fas fa-bolt',
		label: 'label_menu_items_energy',
		labelDefault: 'Energy',
	},
	{
		id: MENU_ITEMS_IDS.eventLog,
		href: '/event_log',
		icon: 'fas fa-notebook',
		label: 'label_menu_items_event_log',
		labelDefault: 'Activity Log',
	},
	{
		id: MENU_ITEMS_IDS.assistant,
		href: '/assistant',
		icon: 'fas fa-sparkles',
		label: 'label_menu_items_assistant',
		labelDefault: 'Shelly AI Assistant',
		mainMenuDock: 'primary',
		hideForSub: true,
	},
];

/** Index of the pinned Assistant slot in `menuItemsNewLayout` / new-layout `menuOrder`. */
export const NEW_LAYOUT_MENU_ORDER_INDEX_ASSISTANT = menuItemsNewLayout.findIndex(
	(item) => item.id === MENU_ITEMS_IDS.assistant,
);

/**
 * Split a new-layout `menuOrder` into reorderable primary indices and the fixed
 * Assistant slot (always last among primary dock icons, before More).
 */
export function partitionNewLayoutMenuOrderIndices(order: readonly number[]): {
	primary: number[];
	assistant: number;
} {
	const assistant = NEW_LAYOUT_MENU_ORDER_INDEX_ASSISTANT;
	const primary: number[] = [];
	const seen = new Set<number>();

	for (const idx of order) {
		if (!Number.isInteger(idx) || idx < 0 || idx >= menuItemsNewLayout.length) continue;
		if (idx === assistant) continue;
		if (seen.has(idx)) continue;
		seen.add(idx);
		primary.push(idx);
	}

	for (let idx = 0; idx < menuItemsNewLayout.length; idx += 1) {
		if (idx === assistant) continue;
		if (!seen.has(idx)) primary.push(idx);
	}

	return { primary, assistant };
}

/** Persistable new-layout order: reorderable primaries with Assistant forced last. */
export function finalizeNewLayoutMenuOrder(primary: readonly number[]): number[] {
	const { primary: normalized } = partitionNewLayoutMenuOrderIndices(primary);
	return [...normalized, NEW_LAYOUT_MENU_ORDER_INDEX_ASSISTANT];
}

export const menuItemsNewLayoutMore: MenuItems = [
	{
		id: MENU_ITEMS_IDS.settings,
		href: '/settings',
		icon: 'fas fa-cog',
		label: 'label_menu_items_settings',
		labelDefault: 'Settings',
	},
	{
		id: MENU_ITEMS_IDS.subscription,
		href: '/settings/premium',
		icon: 'fas fa-gem',
		label: 'collapse_title_subscription',
		labelDefault: 'Subscription',
	},
	{
		id: MENU_ITEMS_IDS.exploreShelly,
		href: '/explore-shelly',
		icon: 'fak fa-solid-shelly',
		label: 'label_menu_items_explore_shelly',
		labelDefault: 'Explore Shelly',
	},
];

export const dropDownMenuItem: MenuItems = [
	{
		id: MENU_ITEMS_IDS.settings,
		href: '/settings',
		icon: 'fa-regular fa-user-pen',
		label: 'button_profile_edit',
		labelDefault: 'Edit Profile',
	},
	{
		id: MENU_ITEMS_IDS.settings,
		href: '/settings/home',
		icon: 'fa-regular fa-house',
		label: 'button_manage_homes',
		labelDefault: 'Manage Homes',
	},
	{
		id: MENU_ITEMS_IDS.subscription,
		href: '/settings/premium',
		icon: 'fas fa-gem',
		label: 'label_subscriptions_tab',
		labelDefault: 'Subscriptions',
	},
];

// Conditionally appended to the "More" dropdown when camera monitoring is enabled for the home.
export const menuItemCameraMonitoring: MenuItemDef = {
	id: MENU_ITEMS_IDS.cameraMonitoring,
	href: '/dash/camera_monitoring',
	icon: 'fas fa-video',
	label: 'submenu_tab_camera_monitoring',
	labelDefault: 'Camera Monitoring',
};

export const menuItemEnergyMonitoring: MenuItemDef = {
	id: MENU_ITEMS_IDS.energyMonitoring,
	href: '/dash/energy_monitoring',
	icon: 'fas fa-bolt',
	label: 'tab_energy_monitoring',
	labelDefault: 'Energy Monitoring',
};

export const tabItemsSettings: TypeTabs = [
	{
		id: 0,
		key: 'user_settings',
		text: 'tab_user_settings',
		defaultText: 'user settings',
		route: '/user',
	},
	{
		id: 5,
		key: 'home_settings',
		text: 'tab_home_settings',
		defaultText: 'home',
		route: '/home',
	},
	{
		id: 1,
		key: 'app_settings',
		text: 'tab_app_settings',
		defaultText: 'app settings',
		route: '/app',
	},
	{
		id: 2,
		key: 'premium',
		text: 'collapse_title_subscription',
		defaultText: 'Subscription',
		route: '/premium',
		circleAvatarIcon: 'fas fa-gem icon-premium-color',
		isPremium: true,
	},
	{
		id: 3,
		key: 'integrations',
		text: 'tab_integrations',
		defaultText: 'Integrations',
		route: '/integrations',
	},
	{
		id: 4,
		key: 'discounts',
		text: 'tab_discounts',
		defaultText: 'Discounts',
		route: '/discounts',
	},
];

export const tabItemsConsumption: TypeTabs = [
	{
		id: 0,
		key: 'current_consumption',
		text: 'tab_energy_item_real_time',
		defaultText: 'real time',
		route: '/-1',
	},
	{
		id: 6,
		key: 'ai_breakdown',
		text: 'tab_energy_item_ai_breakdown',
		defaultText: 'AI Breakdown',
		route: '/6',
		testUser: true,
	},
	{
		id: 1,
		key: 'overall_consumption',
		text: 'tab_energy_item_history',
		defaultText: 'history',
		route: '/0',
	},
	{
		id: 2,
		key: 'monthly_reports',
		text: 'tab_energy_item_monthly_reports',
		defaultText: 'monthly reports',
		route: '/2',
		testUser: false,
		disabled: false,
	},
	// {
	//     id: 3,
	//     key: 'energy_distribution',
	//     text: 'tab_energy_item__distribution',
	//     defaultText: 'energy distribution',
	//     route: '/3',
	//     testUser: true
	// },
	{
		id: 4,
		key: 'power_prices',
		text: 'tab_electricity_tariff',
		defaultText: 'electricity tariff',
		route: '/power_prices',
	},
];

export const tabItemsMyHome = writable<TypeTabs>([
	{ id: 0, key: 'rooms', text: 'tab_all_rooms', defaultText: 'all rooms', route: '/rooms/-1' },
	{ id: 1, key: 'groups', text: 'tab_all_groups', defaultText: 'all groups', route: '/groups/-1' },
	{ id: 2, key: 'scenes', text: 'tab_all_scenes', defaultText: 'all scenes', route: '/scenes/-1' },
	{ id: 3, key: 'devices', text: 'tab_all_devices', defaultText: 'all devices', route: '/devices/-1' },
	{
		id: 4,
		key: 'thermostats',
		text: 'tab_all_thermostats',
		defaultText: 'All Thermostats',
		route: '/thermostats/-1',
	},
	{ id: 5, key: 'alarms', text: 'tab_alarms', defaultText: 'alarms', route: '/alarms/-1' },
]);

export const tabItemsExploreShelly: TypeTabs = [
	{
		id: 0,
		key: 'feedback',
		text: 'feedback_and_help_title',
		defaultText: 'Feedback & Help',
		route: '/feedback',
	},
	{
		id: 1,
		key: 'discounts',
		text: 'tab_discounts',
		defaultText: 'Discounts',
		route: '/discounts',
	},
];

export const tabItemsEventLog: TypeTabs = [
	// {
	// 	id: -10,
	// 	key: 'event_log',
	// 	text: 'tab_activity_log',
	// 	defaultText: 'activity log',
	// 	route: '',
	// },
];

export const dropdownSubmenuData: DrodpwonMenuSubmenuItemsTypes[] = [
	{
		id: 1,
		key: 'rooms',
		isGroupItem: true,
		title: 'label_add_room',
		defaultText: 'Add room',
		icon: 'fa-solid fa-door-open',
		active: false,
	},
	{
		id: 2,
		key: 'groups',
		isGroupItem: true,
		title: 'label_add_group',
		defaultText: 'Add group',
		icon: 'fa-solid fa-network-wired',
		active: false,
	},
	{
		id: 3,
		key: 'scenes',
		isGroupItem: true,
		title: 'label_add_scene',
		defaultText: 'Add scene',
		icon: 'fa-solid fa-film',
		active: false,
	},
	{
		id: 4,
		key: 'thermostat',
		isGroupItem: true,
		title: 'label_add_thermostat',
		defaultText: 'Add thermostat',
		icon: 'fa-solid fa-temperature-three-quarters',
		active: false,
	},
	{
		id: 5,
		key: 'alarms',
		isGroupItem: true,
		title: 'label_add_alarm',
		defaultText: 'Add alarm',
		icon: 'fa-solid fa-shield-halved',
		active: false,
	},
	{
		id: 6,
		key: 'newDash',
		isGroupItem: true,
		title: 'label_add_new_dashboard',
		defaultText: 'Add new dashboard',
		icon: 'fa-solid fa-objects-column',
		active: false,
	},
	{
		id: 7,
		key: 'newDashWidget',
		isGroupItem: false,
		title: 'label_add_dashboard_widget',
		defaultText: 'Add dashboard widget',
		icon: 'fa-solid fa-rectangle-history-circle-plus',
		active: false,
	},
	{
		id: 9,
		key: 'devices',
		isGroupItem: false,
		title: 'label_add_device',
		defaultText: 'Add new device',
		icon: 'fa-solid fa-plus',
		active: false,
	},
];

/**
 * Add-action keys that a shared home can perform directly. Shared homes can
 * only manage their own dashboards/widgets; every other add action stays
 * visible but is intercepted with an owner-managed explanation (FE-2007).
 */
export const SHARED_HOME_ADD_ACTION_KEYS = ['newDash', 'newDashWidget'] as const;

export const checkIsActionDisabled = (item: DrodpwonMenuSubmenuItemsTypes) => {
	const isDashEditPath = get(dashEdit) && get(location).includes('dash');
	const isDashInnerEditPath = get(edit) && get(location).includes('dash/');
	const isEditOnPath = get(edit) && get(location).includes(item.key);

	const isEmptyDeviceList =
		Object.values(get(devices)).length === 0 ||
		Object.values(get(devices)).filter((d: Device) => !d.shared).length === 0;

	if (item.key === 'alarms') {
		const isEmptySensorsList = [...filterSensors(Object.values(get(devices)))].length === 0;
		return isEmptyDeviceList || isEmptySensorsList || isEditOnPath;
	} else if (item.key === 'scenes' || item.key === 'groups') {
		return isEmptyDeviceList || isEditOnPath;
	} else if (item.key === 'thermostat') {
		const isEmptyRoomsList = get(rooms)?.length === 0;
		return isEmptyDeviceList || isEmptyRoomsList;
	} else if (item.key === 'newDash') {
		return isDashEditPath && !isDashInnerEditPath;
	} else if (item.key === 'newDashWidget') {
		return false;
	}

	return isEditOnPath;
};

export const tabItemsPlus: TypeTabs = [];

/** Menu hrefs that open a modal/action instead of navigating to a route. */
const NON_NAVIGABLE_MENU_HREFS = ['/assistant'];

/**
 * Complete destinations that must not get a first-tab or `/-1` list suffix.
 * Energy monitoring is a dedicated dashboard at `/dash/energy_monitoring`;
 * Activity Log is a flat route without a list segment.
 */
export const FLAT_MENU_HREFS = ['/event_log', '/dash/energy_monitoring'] as const;

export function isFlatMenuHref(href: string | undefined): boolean {
	return !!href && (FLAT_MENU_HREFS as readonly string[]).includes(href);
}

/**
 * Index of the menu item whose `href` is the longest prefix of `currentLocation`.
 * Longer prefixes win so `/dash/energy_monitoring` selects Energy, not Dashboard.
 */
export function findActiveMenuItemIndex(
	items: MenuItems,
	currentLocation: string,
	skip?: (item: MenuItemDef) => boolean,
): number {
	let bestIndex = -1;
	let bestLength = -1;
	for (let i = 0; i < items.length; i++) {
		const item = items[i];
		const href = item?.href;
		if (!href || !currentLocation.startsWith(href)) continue;
		if (skip?.(item)) continue;
		if (href.length > bestLength) {
			bestLength = href.length;
			bestIndex = i;
		}
	}
	return bestIndex;
}

/**
 * Resolve the first navigable `href` for the new layout, honouring the user's
 * persisted `menuOrder` (which indexes into `menuItemsNewLayout`; see
 * MenuItemsOrderV2). Skips modal-trigger items (e.g. the assistant) and falls
 * back to the first menu item when nothing else qualifies. Returns the raw item
 * href (e.g. `/dash`, `/home`); callers map it to a concrete route.
 */
export function getNewLayoutLandingItemHref(menuOrderJson: string | undefined): string {
	const { primary } = partitionNewLayoutMenuOrderIndices(
		appendMissingMenuOrderIndices(
			parseHomeDataMenuOrderJson(menuOrderJson),
			menuItemsNewLayout,
		),
	);

	const firstNavigableIndex =
		primary.find((index: number) => {
			const href = menuItemsNewLayout[index]?.href;
			return !!href && !NON_NAVIGABLE_MENU_HREFS.includes(href);
		}) ?? 0;

	return menuItemsNewLayout[firstNavigableIndex]?.href ?? menuItemsNewLayout[0].href;
}

export function getLandingHref() {
	const profileData = get(profile_data);

	// New layout: honour the user's configured first menu item so a fresh login
	// lands there instead of always defaulting to the Dashboard, mirroring the
	// classic layout (FE-1921).
	if (get(showNewLayout)) {
		const currentHref = getNewLayoutLandingItemHref(profileData?.menuOrder);

		if (currentHref === '/home') return '/home/rooms/-1';
		if (isFlatMenuHref(currentHref)) return currentHref;

		return currentHref + '/-1';
	}

	const menuOrder = profileData?.menuOrder ? JSON.parse(profileData?.menuOrder) : mainMenuDefaultOrder;

	let activeMenuItem = menuOrder[0];

	// If first item is non-navigable, fall back to first navigable item
	if (menuItems[activeMenuItem]?.createOnClick) {
		activeMenuItem = menuOrder.find((i: number) => !menuItems[i]?.createOnClick) ?? 0;
	}

	let currentHref = activeMenuItem > 0 ? menuItems[activeMenuItem].href : menuItems[0].href;

	if (currentHref === '/home') {
		currentHref = '/home/rooms';
	}

	// Settings has no `/-1` list route; land on its first tab instead.
	if (currentHref === '/settings') {
		return '/settings/user';
	}

	if (isFlatMenuHref(currentHref)) return currentHref;

	return currentHref + '/-1';
}

export const filteredMenuOrders = (menuOrder: number[]) => {
	// Drop the add-device entry from the rendered list — it lives in the
	// footer dock and is reinserted by the layout, not the persisted order.
	return menuOrder.filter((order) => order !== MAIN_MENU_ORDER_INDEX_ADD_DEVICE);
};

export const setMenuItemsOrder = (menuOrder: number[], menuItems: MenuItems) => {
	//TODO update the menu reorder module, to remove more button from the list of the order items.
	return filteredMenuOrders(menuOrder).map((position: number) => {
		return menuItems[position];
	});
};

/**
 * Settings tabs for MainLayoutNew tab bar.
 *
 * The Discounts tab is classic-layout only: in the new layout discounts live
 * under "Explore Shelly" (see `tabItemsExploreShelly`), so it is filtered out
 * here. Reading `showNewLayout` at call time keeps the tab bar consistent when
 * switching between accounts whose homes use different layouts.
 */
export function getMainLayoutSettingsTabs(): TypeTabs {
	if (get(showNewLayout)) {
		return tabItemsSettings
			.filter((tab) => tab.key !== 'discounts')
			.map((tab) => {
				if (tab.key === 'user_settings') {
					return { ...tab, text: 'tab_profile_settings', defaultText: 'Profile Settings' };
				}
				if (tab.key === 'home_settings') {
					return { ...tab, text: 'tab_home_settings', defaultText: 'Home Settings' };
				}
				if (tab.key === 'app_settings') {
					return { ...tab, text: 'tab_general_settings', defaultText: 'General Settings' };
				}
				return tab;
			});
	}
	return tabItemsSettings;
}

/**
 * Full path when opening a "More" main-menu item: base href + first sub-tab route.
 * Matches MainLayoutNew.handlerClickMenuItem for menuItemsNewLayoutMore entries.
 */
export function getMoreMenuItemNavigateHref(menuItem: MenuItemDef): string {
	const href = menuItem.href ?? '';
	const id = menuItem.id;
	let firstTabRoute = '';
	if (id === MENU_ITEMS_IDS.settings) {
		firstTabRoute = getMainLayoutSettingsTabs()[0]?.route ?? '';
	} else if (id === MENU_ITEMS_IDS.exploreShelly) {
		firstTabRoute = tabItemsExploreShelly[0]?.route ?? '';
	}
	return href + firstTabRoute;
}

/**
 * Resolve the menu item whose `href` is the longest prefix of `currentLocation`,
 * used to derive the header screen title. Searches classic rail + "More"
 * entries; skips the assistant (modal trigger) and `createOnClick` actions.
 */
export const getHeaderMenuItemDef = (currentLocation: string): MenuItemDef | undefined => {
	const candidates = [...menuItems, ...menuItemsNewLayoutMore];
	const index = findActiveMenuItemIndex(candidates, currentLocation, (item) => {
		if (item.label === 'label_menu_items_assistant') return true;
		if (item.createOnClick) return true;
		return false;
	});
	return index > -1 ? candidates[index] : undefined;
};

export const filterMenuItemsForSub = (items: MenuItems, isSub: boolean): MenuItems => {
	if (!isSub) return items;
	return items.filter((item) => !item.hideForSub);
};
